import React, { useMemo, useState } from 'react';
import { graphql, usePreloadedQuery } from 'react-relay';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/new';
import { Checkbox } from '@atlaskit/checkbox';
import Form, {
	RequiredAsterisk,
	ErrorMessage,
	Field,
	FormFooter,
	FormSection,
} from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { Radio, RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import Select, { type OptionType, type ValueType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { fg } from '@atlassian/jira-feature-gating';
import { SuccessAutoDismissFlag, FlagGroup } from '@atlassian/jira-flags';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { settingsFormV2Query } from '@atlassian/jira-relay/src/__generated__/settingsFormV2Query.graphql';
import { CollapsibleGroup } from '@atlassian/jira-servicedesk-insight-shared-ui/src/ui/collapsible-group/index.tsx';
import { EmbedCode } from '../../../common/embed-code-v2';
import type { ConfigType } from '../../../common/types';
import { BACKGROUND_PATTERN_OPTIONS, LIMITS } from './constants';
import messages from './messages';
import type { SettingsFormProps } from './types';
import {
	getRequestOption,
	hexColorValidator,
	requiredWithMaxLengthValidator,
	transformHexValue,
	useWidgetSettingsForm,
} from './utils';

export const SettingsFormV2 = ({
	config,
	onChange,
	projectKey,
	onSubmit,
	submitting,
	isFormSubmitSuccess,
	handleSuccessDismiss,
	isEnableDisabled,
	isRequestTypeValidationLoading,
	embedCode,
	queryReference,
}: SettingsFormProps) => {
	const { formatMessage } = useIntl();
	const [tooltipTrigger, setTooltipTrigger] = useState(false);
	const [
		configState,
		initialConfig,
		validateSpy,
		requestTypesState,
		fetchRequestTypes,
		onFormSubmit,
		isFormDirty,
	] = useWidgetSettingsForm({
		config,
		onChange,
		formatMessage,
		projectKey,
		onSubmit,
		isEnableDisabled,
	});

	const WidgetTypeValue = {
		BASIC: 'BASIC',
		VIRTUAL_AGENT: 'VIRTUAL_AGENT',
	};

	const data = usePreloadedQuery<settingsFormV2Query>(
		graphql`
			query settingsFormV2Query($cloudId: ID!, $projectKey: String!) {
				virtualAgent {
					virtualAgentEntitlements(cloudId: $cloudId) @optIn(to: ["VirtualAgent"]) {
						isAiEnabledInAdminHub
						isVirtualAgentAvailable
					}
				}
				jira {
					jiraProjectByKey(cloudId: $cloudId, key: $projectKey) @optIn(to: ["jiraProjectByKey"]) {
						id
						virtualAgentConfiguration {
							... on VirtualAgentConfiguration {
								id
							}
						}
					}
				}
			}
		`,
		queryReference,
	);
	const ignoreAIEnableCheck = !ff('check-for-ai-enabling-in-get-premium-banner_oxh6r');
	const isVaConfigured =
		data?.virtualAgent?.virtualAgentEntitlements?.isVirtualAgentAvailable &&
		(ignoreAIEnableCheck || data?.virtualAgent?.virtualAgentEntitlements?.isAiEnabledInAdminHub) &&
		data.jira?.jiraProjectByKey?.virtualAgentConfiguration != null;

	const requestOptions = useMemo(
		() =>
			requestTypesState.data?.map((requestType) => ({
				label: requestType.name,
				value: requestType.id,
			})) || [],
		[requestTypesState.data],
	);

	// only show this error message if fetching request type fails for the first time
	if (requestTypesState.error && !requestTypesState.data) {
		return (
			<SectionMessage appearance="error">
				{formatMessage(messages.errorFetchingRequestTypes)}
			</SectionMessage>
		);
	}

	if (!requestTypesState.data || !configState.requestTypeId) {
		return (
			<Box xcss={loadingContainerStyles}>
				<Spinner
					size="large"
					testId="servicedesk-widget-settings.ui.content.settings-form-v2.spinner"
				/>
			</Box>
		);
	}

	const isSaveDisabled =
		isRequestTypeValidationLoading ||
		requestTypesState.loading ||
		!!requestTypesState.error ||
		(initialConfig.requestTypeId !== null && !isFormDirty) ||
		isEnableDisabled;

	const handleSubmit = () => {
		onFormSubmit();
	};

	function kbArticleToggle() {
		return (
			<Field
				name="kbEnabled"
				validate={validateSpy('kbEnabled')}
				defaultValue={initialConfig.kbEnabled}
			>
				{({ fieldProps: { value, ...rest } }) => (
					<Inline alignBlock="center" xcss={toggleKBArticleMargin}>
						<Toggle
							isChecked={value}
							{...rest}
							isDisabled={config?.widgetType === WidgetTypeValue.VIRTUAL_AGENT}
						/>
						<Text>{formatMessage(messages.allowSearchForKnowledgeBaseArticlesToggleLabel)}</Text>
					</Inline>
				)}
			</Field>
		);
	}
	function hiddenBadge(): React.ReactNode {
		return (
			<Box xcss={badgeStyles}>
				<Inline alignInline="start" space="space.075" alignBlock="center">
					<Lozenge appearance="moved">{formatMessage(messages.hiddenWidgetBadgeLabel)}</Lozenge>
				</Inline>
			</Box>
		);
	}
	function rtSearchToggle() {
		return (
			<Field
				name="rtSearchEnabled"
				validate={validateSpy('rtSearchEnabled')}
				defaultValue={initialConfig.rtSearchEnabled}
			>
				{({ fieldProps: { value, ...rest } }) => (
					<Inline alignBlock="center">
						<Toggle
							isChecked={value}
							{...rest}
							isDisabled={config?.widgetType === WidgetTypeValue.VIRTUAL_AGENT}
						/>
						<Text>{formatMessage(messages.allowSearchForAllRequestTypesToggleLabel)}</Text>
					</Inline>
				)}
			</Field>
		);
	}

	return (
		<Form<ConfigType> onSubmit={handleSubmit}>
			{({ formProps, reset }) => (
				<form {...formProps} aria-label={formatMessage(messages.formAriaLabel)}>
					<FormSection>
						<Inline xcss={requiredMessageStyles}>
							<Text size="small">
								{formatMessage(messages.requiredFieldsInstructionText)}
								<RequiredAsterisk />
							</Text>
						</Inline>
						<Heading size="small">{formatMessage(messages.supportExperienceHeading)}</Heading>
						<Field
							name="widgetType"
							validate={validateSpy('widgetType')}
							defaultValue={
								initialConfig.widgetType === WidgetTypeValue.VIRTUAL_AGENT
									? WidgetTypeValue.VIRTUAL_AGENT
									: WidgetTypeValue.BASIC
							}
						>
							{({ fieldProps: { value, ...rest } }) => (
								<>
									<Stack space="space.150">
										<Stack xcss={containerStyles} space="space.100">
											<Box xcss={inlineStyles}>
												<Inline space="space.075" alignBlock="center">
													<Radio
														value={WidgetTypeValue.BASIC}
														isChecked={value === WidgetTypeValue.BASIC}
														{...rest}
													/>
													<Heading size="xsmall">
														{formatMessage(messages.basicWidgetLabel)}
													</Heading>
												</Inline>
											</Box>
											<Box xcss={toggleStyles}>
												<Text as="span">{formatMessage(messages.basicWidgetDescription)}</Text>
												<Box xcss={toggleMargin}>
													{rtSearchToggle()}
													{kbArticleToggle()}
												</Box>
											</Box>
										</Stack>
										{isVaConfigured ? (
											<Stack xcss={containerStyles} space="space.100">
												<Box xcss={inlineStyles}>
													<Inline space="space.075" alignBlock="center">
														<Radio
															value={WidgetTypeValue.VIRTUAL_AGENT}
															isChecked={value === WidgetTypeValue.VIRTUAL_AGENT}
															{...rest}
														/>
														<Heading size="xsmall">
															{fg('virtual_agent_rebrand_to_virtual_service_agent')
																? formatMessage(messages.virtualServiceAgentLabel)
																: formatMessage(messages.virtualAgentLabel)}
														</Heading>
														<Lozenge appearance="new">
															{formatMessage(messages.virtualAgentBetaLabel)}
														</Lozenge>
													</Inline>
												</Box>
												<Box xcss={extraInfoStyles}>
													<Stack alignInline="start">
														<Text as="span">
															<FormattedMessage
																{...messages.virtualAgentDescription}
																values={{ br: <br /> }}
															/>
														</Text>
														<Anchor href="virtual-agent" xcss={configureLinkStyles}>
															{fg('virtual_agent_rebrand_to_virtual_service_agent')
																? formatMessage(
																		messages.virtualServiceAgentTooltipConfigureHeadingLabel,
																	)
																: formatMessage(messages.virtualAgentTooltipConfigureHeadingLabel)}
														</Anchor>
													</Stack>
												</Box>
											</Stack>
										) : (
											<Stack xcss={containerStyles} space="space.100">
												<Box xcss={inlineStyles}>
													<Inline space="space.075" alignBlock="center">
														<Popup
															isOpen={tooltipTrigger}
															onClose={() => setTooltipTrigger(false)}
															placement="left-start"
															content={() => (
																<Box xcss={tooltipContentStyles}>
																	{fg('virtual_agent_rebrand_to_virtual_service_agent')
																		? formatMessage(messages.virtualServiceAgentTooltipLabel)
																		: formatMessage(messages.virtualAgentTooltipLabel)}
																	<Anchor href="virtual-agent" xcss={configureLinkStyles}>
																		{fg('virtual_agent_rebrand_to_virtual_service_agent')
																			? formatMessage(
																					messages.virtualServiceAgentTooltipConfigureLinkLabel,
																				)
																			: formatMessage(
																					messages.virtualAgentTooltipConfigureLinkLabel,
																				)}
																	</Anchor>
																</Box>
															)}
															trigger={(triggerProps) => (
																<Radio
																	value={WidgetTypeValue.VIRTUAL_AGENT}
																	isChecked={value === WidgetTypeValue.VIRTUAL_AGENT}
																	isDisabled
																	onMouseOver={() => setTooltipTrigger(true)}
																	{...triggerProps}
																/>
															)}
														/>
														<Heading size="xsmall">
															{fg('virtual_agent_rebrand_to_virtual_service_agent')
																? formatMessage(messages.virtualServiceAgentLabel)
																: formatMessage(messages.virtualAgentLabel)}
														</Heading>
														<Lozenge appearance="new">
															{formatMessage(messages.virtualAgentBetaLabel)}
														</Lozenge>
													</Inline>
												</Box>
												<Box xcss={extraInfoStyles}>
													<Stack alignInline="start">
														<Text as="span">
															<FormattedMessage
																{...messages.virtualAgentDescription}
																values={{ br: <br /> }}
															/>
														</Text>
														<Anchor href="virtual-agent" xcss={configureLinkStyles}>
															{fg('virtual_agent_rebrand_to_virtual_service_agent')
																? formatMessage(
																		messages.virtualServiceAgentTooltipConfigureHeadingLabel,
																	)
																: formatMessage(messages.virtualAgentTooltipConfigureHeadingLabel)}
														</Anchor>
													</Stack>
												</Box>
											</Stack>
										)}
									</Stack>
								</>
							)}
						</Field>

						<Box xcss={dividerStyles} />
						{config?.widgetType === WidgetTypeValue.BASIC ? (
							<Box paddingBlockStart="space.200">
								<CollapsibleGroup
									title={formatMessage(messages.defaultRequestTypeHeading)}
									isDefaultCollapsed
								>
									<Text as="p">
										{fg('virtual_agent_rebrand_to_virtual_service_agent')
											? formatMessage(messages.defaultRequestTypeSelectHelpTextV2)
											: formatMessage(messages.defaultRequestTypeSelectHelpText)}
									</Text>
									<Field<ValueType<OptionType | number>>
										name="requestTypeId"
										label={formatMessage(messages.defaultRequestTypeHeading)}
										isRequired
										defaultValue={getRequestOption(
											requestTypesState.data,
											initialConfig.requestTypeId || configState.requestTypeId,
										)}
										validate={validateSpy('requestTypeId')}
									>
										{({ fieldProps, error }) => (
											<>
												<Select
													options={requestOptions}
													{...fieldProps}
													isLoading={requestTypesState.loading || isRequestTypeValidationLoading}
													isSearchable
													onInputChange={fetchRequestTypes}
													filterOption={() => true} // removing default filtering as we filter from api call
												/>
												{error && <ErrorMessage>{error}</ErrorMessage>}
											</>
										)}
									</Field>
									{requestTypesState.error && (
										<ErrorMessage>{formatMessage(messages.errorFetchingRequestTypes)}</ErrorMessage>
									)}
								</CollapsibleGroup>
							</Box>
						) : null}
						<Box paddingBlockStart="space.200">
							<CollapsibleGroup
								title={formatMessage(messages.lookAndFeelHeading)}
								isDefaultCollapsed
							>
								<Field
									name="primaryColorHex"
									label={
										<Text color="color.text.subtle" weight="medium" size="small">
											{formatMessage(messages.colorInputLabel)}
										</Text>
									}
									defaultValue={initialConfig.primaryColorHex}
									validate={validateSpy('primaryColorHex', hexColorValidator)}
									transform={transformHexValue}
									isRequired
								>
									{({ fieldProps, error }) => (
										<>
											<Inline alignBlock="center" space="space.100">
												<Box
													xcss={colorPreviewStyles}
													// eslint-disable-next-line jira/react/no-style-attribute
													style={{
														backgroundColor: fieldProps.value,
													}}
												/>
												<TextField {...fieldProps} maxLength={7} />
											</Inline>

											{error && <ErrorMessage>{error}</ErrorMessage>}
										</>
									)}
								</Field>

								<Field<ValueType<OptionType | string>>
									name="backgroundPattern"
									label={
										<Text color="color.text.subtle" weight="medium" size="small">
											{formatMessage(messages.widgetBackgroundPatternSelectLabel)}
										</Text>
									}
									defaultValue={BACKGROUND_PATTERN_OPTIONS.find(
										(v) => v.value === initialConfig.backgroundPattern,
									)}
									validate={validateSpy('backgroundPattern')}
								>
									{({ fieldProps }) => (
										<Select options={BACKGROUND_PATTERN_OPTIONS} {...fieldProps} />
									)}
								</Field>

								<Field
									name="widgetButtonTextEnabled"
									label={
										<Text color="color.text.subtle" weight="medium" size="small">
											{formatMessage(messages.widgetButtonTypeRadioGroupLabel)}
										</Text>
									}
									validate={validateSpy('widgetButtonTextEnabled')}
									defaultValue={initialConfig.widgetButtonTextEnabled ? 'text' : 'icon'}
								>
									{({ fieldProps }) => (
										<RadioGroup
											options={[
												{
													name: 'icon',
													value: 'icon',
													label: formatMessage(messages.widgetButtonQuestionMarkIconRadioLabel),
												},
												{
													name: 'text',
													value: 'text',
													label: formatMessage(messages.widgetButtonCustomTextRadioLabel),
												},
											]}
											{...fieldProps}
										/>
									)}
								</Field>

								<Field
									name="widgetButtonText"
									label={
										<Text color="color.text.subtle" weight="medium" size="small">
											{formatMessage(messages.widgetButtonCustomTextInputLabel)}
										</Text>
									}
									defaultValue={initialConfig.widgetButtonText}
									isDisabled={!configState.widgetButtonTextEnabled}
									validate={validateSpy(
										'widgetButtonText',
										requiredWithMaxLengthValidator(
											formatMessage(messages.widgetButtonCustomTextInputLabel),
											LIMITS.WIDGET_BUTTON_CUSTOM_TEXT_MAX_LENGTH,
										),
									)}
									isRequired
								>
									{({ fieldProps, error }) => (
										<>
											<TextField {...fieldProps} />
											{error && <ErrorMessage>{error}</ErrorMessage>}
										</>
									)}
								</Field>
								<Box xcss={innerDividerStyles} />
								<Field
									name="headerText"
									label={
										<Text color="color.text.subtle" weight="medium" size="small">
											{formatMessage(messages.widgetHeaderInputLabel)}
										</Text>
									}
									defaultValue={initialConfig.headerText}
									validate={validateSpy(
										'headerText',
										requiredWithMaxLengthValidator(
											formatMessage(messages.widgetHeaderInputLabel),
											LIMITS.HEADER_TEXT_MAX_LENGTH,
										),
									)}
									isRequired
								>
									{({ fieldProps, error }) => (
										<>
											<TextField {...fieldProps} />
											<FieldFooter>
												{formatMessage(messages.widgetHeaderFieldCaptionLabel)}
											</FieldFooter>
											{error && <ErrorMessage>{error}</ErrorMessage>}
										</>
									)}
								</Field>
								{config?.widgetType === WidgetTypeValue.BASIC ? (
									<Field<string, HTMLTextAreaElement>
										name="confirmationText"
										label={
											<Text color="color.text.subtle" weight="medium" size="small">
												{formatMessage(messages.confirmationMessageInputLabel)}
											</Text>
										}
										defaultValue={initialConfig.confirmationText}
										validate={validateSpy(
											'confirmationText',
											requiredWithMaxLengthValidator(
												formatMessage(messages.confirmationMessageInputLabel),
												LIMITS.CONFIRMATION_TEXT_MAX_LENGTH,
											),
										)}
										isRequired
									>
										{({ fieldProps, error }) => (
											<>
												<TextArea resize="smart" minimumRows={1} {...fieldProps} />
												<FieldFooter>
													{formatMessage(messages.widgetConfirmationCaptionLabel)}
												</FieldFooter>
												{error && <ErrorMessage>{error}</ErrorMessage>}
											</>
										)}
									</Field>
								) : null}
							</CollapsibleGroup>
						</Box>
						<Box paddingBlockStart="space.200">
							<CollapsibleGroup title={formatMessage(messages.embedCodeHeading)} isDefaultCollapsed>
								<EmbedCode embedCode={embedCode} />
							</CollapsibleGroup>
						</Box>
						<Field<boolean>
							name="widgetEnabled"
							validate={validateSpy('widgetEnabled')}
							defaultValue={isEnableDisabled ? false : initialConfig.widgetEnabled}
							isDisabled={isEnableDisabled}
						>
							{({ fieldProps: { value, ...rest } }) => (
								<Box paddingBlockStart="space.100">
									<CollapsibleGroup
										showHeaderItemWhenOpen
										headerItem={value ? hiddenBadge() : null}
										title={formatMessage(messages.hideWidgetHeading)}
										isDefaultCollapsed
									>
										<Stack space="space.100">
											<Text as="span">{formatMessage(messages.hideWidgetCheckboxDescription)}</Text>
											<Checkbox
												label={formatMessage(messages.hideWidgetCheckboxLabel)}
												size="large"
												isChecked={value}
												{...rest}
											/>
										</Stack>
									</CollapsibleGroup>
								</Box>
							)}
						</Field>
						{isFormSubmitSuccess && (
							<FlagGroup onDismissed={() => handleSuccessDismiss(false)}>
								<SuccessAutoDismissFlag
									key="settings-saved-flag"
									title={formatMessage(messages.saveSuccessFlagHeading)}
									id="settings-form-v2-settings-saved-flag"
								/>
							</FlagGroup>
						)}
					</FormSection>
					<Box xcss={dividerStyles} />
					<FormFooter align="end">
						<ButtonGroup>
							<Button appearance="subtle" onClick={() => reset()} isDisabled={!isFormDirty}>
								{formatMessage(messages.cancelButtonLabel)}
							</Button>

							<LoadingButton
								type="submit"
								appearance="primary"
								isLoading={submitting}
								isDisabled={isSaveDisabled}
							>
								{formatMessage(messages.saveButtonLabel)}
							</LoadingButton>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default SettingsFormV2;

const FieldFooter = ({ children }: { children: string }) => (
	<Box xcss={footerStyles}>{children}</Box>
);
const colorPreviewStyles = xcss({
	borderRadius: '4px',
	minHeight: 'size.400',
	minWidth: 'size.400',
	borderWidth: 'border.width.outline',
	borderColor: 'color.border',
	borderStyle: 'solid',
});

const loadingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	minHeight: 'size.1000',
});

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: 'elevation.surface.raised',
	padding: 'space.150',
	transition: '200ms',
	borderRadius: '6px',
	border: '1px solid ',
	borderColor: 'color.border',
});

const inlineStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const toggleStyles = xcss({
	paddingLeft: 'space.400',
});

const toggleMargin = xcss({
	marginLeft: 'space.negative.075',
});

const toggleKBArticleMargin = xcss({
	marginTop: 'space.negative.075',
});

const extraInfoStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	paddingLeft: 'space.300',
	marginLeft: 'space.100',
});

const dividerStyles = xcss({
	height: '1px',
	backgroundColor: 'color.background.accent.gray.subtle.pressed',
	width: '100%',
	marginTop: 'space.300',
	marginBottom: 'space.100',
});

const innerDividerStyles = xcss({
	height: '1px',
	backgroundColor: 'color.background.accent.gray.subtle.pressed',
	width: '100%',
	marginTop: 'space.200',
	marginBottom: 'space.100',
});

const badgeStyles = xcss({
	width: '480px',
});

const footerStyles = xcss({
	color: 'color.text.subtlest',
	font: token('font.body.UNSAFE_small'),
});

const tooltipContentStyles = xcss({
	paddingInline: 'space.200',
	paddingBlock: 'space.150',
	width: '206px',
});

const configureLinkStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',

	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

const requiredMessageStyles = xcss({
	marginBottom: 'space.150',
});
